/**
 * Routing-map stops from a live tour. Only days with real venue/bus coords
 * are plotted — no invented cities or FlightAware pins.
 */
import { hasValidMapCoords } from './mapCoords';
import {
  estimateTravelBetween,
  getDayEndStop,
  getInterDayTravelSummary,
  getVenueCoords,
  venueHasLodgingFlag,
} from './tourDayLodgingUtils';

export function flyModeFromTravel(travel) {
  const mode = String(travel?.mode || travel?.modeLabel || '').toLowerCase();
  return mode.includes('flight') || mode.includes('fly') || mode === 'air';
}

/** Prefer the show venue; skip lodging when another pin exists. */
export function coordsForTourDay(day) {
  const venues = Array.isArray(day?.venues) ? day.venues : [];
  const lodgingId = String(day?.lodgingVenueId || '').trim();
  for (let i = 0; i < venues.length; i += 1) {
    const v = venues[i];
    if (lodgingId && String(v?.id || '') === lodgingId && venues.length > 1) continue;
    if (venueHasLodgingFlag(v) && venues.length > 1) continue;
    const c = getVenueCoords(v);
    if (!c || !hasValidMapCoords(c)) continue;
    const city = [v.city, v.state].filter(Boolean).join(', ');
    return {
      lat: c.lat,
      lng: c.lng,
      city: city || String(v.name || '').trim(),
      venue: String(v.name || '').trim(),
    };
  }
  const end = getDayEndStop(day);
  if (end?.coords && hasValidMapCoords(end.coords)) {
    return {
      lat: end.coords.lat,
      lng: end.coords.lng,
      city: String(end.label || '').trim(),
      venue: String(end.label || '').trim(),
    };
  }
  return null;
}

export function buildRoutingStops(tour) {
  const days = Array.isArray(tour?.days) ? tour.days : [];
  const raw = [];
  days.forEach((day, dayIndex) => {
    const dateKey = String(day?.scheduledDate || '').trim();
    if (!dateKey) return;
    const loc = coordsForTourDay(day);
    if (!loc) return;
    const n = Number(String(dateKey).slice(-2));
    raw.push({
      dateKey,
      n: Number.isFinite(n) ? n : raw.length + 1,
      lat: loc.lat,
      lng: loc.lng,
      city: loc.city || 'Stop',
      venue: loc.venue || '',
      dayIndex,
    });
  });
  return raw.map((stop, i) => {
    if (i === 0) return { ...stop, mode: 'bus', miles: 0 };
    const prev = raw[i - 1];
    const estimate = estimateTravelBetween(
      { lat: prev.lat, lng: prev.lng },
      { lat: stop.lat, lng: stop.lng },
      'drive'
    );
    let fly = false;
    const modes = Array.isArray(tour?.interDayLegModes) ? tour.interDayLegModes : [];
    for (let d = prev.dayIndex + 1; d <= stop.dayIndex; d += 1) {
      if (flyModeFromTravel({ mode: modes[d - 1] }) || flyModeFromTravel(getInterDayTravelSummary(tour, d))) {
        fly = true;
      }
    }
    return {
      ...stop,
      mode: fly ? 'fly' : 'bus',
      miles: estimate && !estimate.same ? Number(estimate.miles) || 0 : 0,
    };
  });
}

export function summarizeRoutingStops(stops) {
  const list = Array.isArray(stops) ? stops : [];
  const miles = list.reduce((sum, s, i) => (i === 0 ? sum : sum + (Number(s.miles) || 0)), 0);
  return { stopCount: list.length, miles: Math.round(miles) };
}

/** Same day-route palette as Tour Planner. */
export const PLANNER_ROUTE_COLORS = [
  '#2c7be5',
  '#9b59b6',
  '#27ae60',
  '#3498db',
  '#8e44ad',
  '#16a085',
  '#2980b9',
  '#2ecc71',
];

function plannerLegMode(raw) {
  const m = String(raw || 'drive').toLowerCase();
  if (m.includes('flight') || m.includes('fly') || m === 'air') return 'flight';
  if (m.includes('train') || m.includes('rail')) return 'train';
  return 'drive';
}

function samePoint(a, b) {
  return Math.abs(Number(a?.latitude) - Number(b?.latitude)) < 1e-5
    && Math.abs(Number(a?.longitude) - Number(b?.longitude)) < 1e-5;
}

/** Every mapped venue on a Tour Planner day, not just the show pin. */
export function venuesWithCoords(day) {
  const list = Array.isArray(day?.venues) ? day.venues : [];
  const out = [];
  list.forEach((venue, i) => {
    const c = getVenueCoords(venue);
    if (!c || !hasValidMapCoords(c)) return;
    out.push({
      id: String(venue.id || `${day?.scheduledDate || 'd'}-${i}`),
      name: String(venue.name || '').trim() || 'Stop',
      city: String(venue.city || '').trim(),
      state: String(venue.state || '').trim(),
      latitude: c.lat,
      longitude: c.lng,
    });
  });
  return out;
}

/**
 * Tour Planner map model: all venues + within-day and inter-day legs.
 * Calendar Map tab draws this the same way Tour Planner draws a loaded tour.
 */
export function buildPlannerMapModel(tour) {
  const days = Array.isArray(tour?.days) ? tour.days : [];
  const interModes = Array.isArray(tour?.interDayLegModes) ? tour.interDayLegModes : [];
  const tourDays = [];
  days.forEach((day, dayIndex) => {
    const venues = venuesWithCoords(day);
    if (!venues.length) return;
    tourDays.push({
      id: String(day.id || day.scheduledDate || `day-${dayIndex}`),
      dateKey: String(day.scheduledDate || '').trim(),
      dayIndex,
      colorIndex: tourDays.length,
      venues,
      legModes: Array.isArray(day.legModes) ? day.legModes : [],
    });
  });

  const legs = [];
  tourDays.forEach((day, i) => {
    day.venues.forEach((venue, v) => {
      if (v === 0) return;
      const from = day.venues[v - 1];
      if (samePoint(from, venue)) return;
      legs.push({
        key: `day-${day.id}-${v - 1}`,
        from,
        to: venue,
        mode: plannerLegMode(day.legModes[v - 1] || 'drive'),
        colorIndex: day.colorIndex,
        kind: 'day',
      });
    });
    if (i === 0) return;
    const prevDay = tourDays[i - 1];
    const from = prevDay.venues[prevDay.venues.length - 1];
    const to = day.venues[0];
    if (!from || !to || samePoint(from, to)) return;
    let fly = false;
    for (let d = prevDay.dayIndex + 1; d <= day.dayIndex; d += 1) {
      if (flyModeFromTravel({ mode: interModes[d - 1] }) || flyModeFromTravel(getInterDayTravelSummary(tour, d))) {
        fly = true;
      }
    }
    legs.push({
      key: `inter-${prevDay.id}-${day.id}`,
      from,
      to,
      mode: fly ? 'flight' : plannerLegMode(interModes[prevDay.dayIndex] || 'drive'),
      colorIndex: day.colorIndex,
      kind: 'inter',
    });
  });

  const venues = tourDays.flatMap((day) => day.venues.map((venue) => ({
    ...venue,
    colorIndex: day.colorIndex,
    dateKey: day.dateKey,
  })));

  return { tourDays, legs, venues };
}
